import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getSystemAlerts } from '../../../utils/dashboardService';

const SystemAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        setLoading(true);
        const data = await getSystemAlerts();
        setAlerts(data || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching system alerts:', err);
        setError('Failed to load alerts');
      } finally {
        setLoading(false);
      }
    };

    fetchAlerts();
  }, []);

  const getAlertIcon = (type) => {
    switch (type) {
      case 'error':
        return 'AlertCircle';
      case 'warning':
        return 'AlertTriangle';
      case 'info':
        return 'Info';
      default:
        return 'Bell';
    }
  };

  const getAlertColor = (type) => {
    switch (type) {
      case 'error':
        return 'text-destructive bg-destructive/10 border-destructive/20';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200 dark:text-yellow-400 dark:bg-yellow-900/20 dark:border-yellow-800/30';
      case 'info':
        return 'text-blue-600 bg-blue-50 border-blue-200 dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-800/30';
      default:
        return 'text-muted-foreground bg-muted/10 border-border';
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">System Alerts</h3>
        </div>
        <div className="space-y-3">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-muted rounded-full"></div>
                <div className="flex-1">
                  <div className="w-32 h-4 bg-muted rounded mb-2"></div>
                  <div className="w-48 h-3 bg-muted rounded"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">System Alerts</h3>
        </div>
        <div className="text-center py-8">
          <Icon name="AlertTriangle" size={24} className="text-destructive mx-auto mb-2" />
          <p className="text-destructive font-medium">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">System Alerts</h3>
          <p className="text-sm text-muted-foreground">Critical issues requiring attention</p>
        </div>
        {alerts.length > 0 && (
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-destructive rounded-full animate-pulse"></div>
            <span className="text-sm text-destructive font-medium">{alerts.length} alert{alerts.length !== 1 ? 's' : ''}</span>
          </div>
        )}
      </div>

      {alerts.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="CheckCircle" size={48} className="text-accent mx-auto mb-4" />
          <h4 className="text-lg font-medium text-foreground mb-2">All Systems Operational</h4>
          <p className="text-sm text-muted-foreground">No critical alerts at this time</p>
        </div>
      ) : (
        <div className="space-y-4">
          {alerts.map((alert) => (
            <div
              key={alert.id}
              className={`border rounded-lg p-4 ${getAlertColor(alert.type)}`}
            >
              <div className="flex items-start space-x-3">
                <Icon 
                  name={getAlertIcon(alert.type)} 
                  size={20} 
                  className="mt-0.5 flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium mb-1">{alert.title}</h4>
                  <p className="text-sm opacity-90 mb-3">{alert.message}</p>
                  {alert.actionUrl && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.location.href = alert.actionUrl}
                      className="text-xs"
                    >
                      Take Action
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SystemAlerts;
